import '@/styles/app'
import 'virtual:mdi-icons-round.css'
import 'virtual:tmi-icons.css'
import { Quasar } from 'quasar'
import i18n from '@widgets/shared/locales/i18n'
import sentryMonitoringService from '@widgets/shared/services/monitoring/sentryMonitoringService'
import messengerApiService from '@widgets/messenger/services/messengerApiService'
import { getUniqueCode } from '@widgets/shared/compositions/uniqueCode'
import MessengerContainer from '@widgets/messenger/containers/MessengerContainer.vue'
import { getParentUrl } from '@widgets/messenger/utils/parentUrlMessages'
import type { MessengerSettings } from '@widgets/messenger/types'
import { getIsPreview } from '@widgets/shared/compositions/isPreview'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { subscribeOnMessengerTabActivityMessages } from '@widgets/messenger/utils/tabActivity'
import { messengerBroadcastEventEmitterService } from '@widgets/messenger/services/messengerBroadcastEventEmitterService'
import { eventEmitterService } from '@widgets/shared/services/eventEmitterService'
import { messengerEmojiLoadedEventName } from '@widgets/messenger/const'
import soundService from '@widgets/shared/services/soundService'
import imageService from '@widgets/shared/services/imageService'
import { registerSentryInterceptor } from '@widgets/shared/utils/registerSentryInterceptor'
import { createApp } from '@/composition/vue/compositionApi'
import quasarOptions from '@/quasarOptions'
import { safeHtmlDirective } from '@/directives/safe-html/safe-html.directive'
import { isSentryOn } from '@/utils/system'
import { loadEmojiData } from '@/utils/emojiHelpers'

const run = async () => {
  if (isSentryOn()) {
    sentryMonitoringService.init('widget-messenger')
  }

  const uniqueCode = getUniqueCode()
  const isPreview = getIsPreview()
  const parentUrl = (await getParentUrl(uniqueCode)) ?? window.location.href
  const domain = new URL(parentUrl).hostname

  if (isSentryOn()) {
    sentryMonitoringService.setExtras({
      uniqueCode,
      isPreview,
      parentUrl,
      domain,
    })
  }

  messengerApiService.init({
    requestUrl: parentUrl,
    isPreview,
  })

  if (isSentryOn()) {
    registerSentryInterceptor(messengerApiService)
  }

  messengerBroadcastEventEmitterService.init({
    uniqueCode,
    domain,
  })
  soundService.warmupNotificationSound()

  let settings: MessengerSettings
  try {
    settings = await messengerApiService.getSettings(uniqueCode)
    if (settings.logoUrl) {
      imageService.warmupImage(settings.logoUrl)
    }
  } catch (error) {
    // BE response 403 or 404 when the widget is not available due to targeting or the widget status
    if (messengerApiService.isForbidenError(error) || messengerApiService.isNotFoundError(error)) {
      return
    }
    throw error
  }

  const app = createApp(MessengerContainer)

  const widgetStore = useWidgetStore(app)
  widgetStore.init({
    widgetSettings: settings,
    uniqueCode,
    domain,
    parentUrl,
  })

  const changeActiveTabEventCode = 'changeActiveTab'
  subscribeOnMessengerTabActivityMessages(uniqueCode, (isActive: boolean) => {
    widgetStore.setIsActiveTab(isActive)
    if (isActive) {
      widgetStore.setIsLastActiveTab(true)
      messengerBroadcastEventEmitterService.emit(changeActiveTabEventCode)
    }
  })
  messengerBroadcastEventEmitterService.on(changeActiveTabEventCode, () => {
    widgetStore.setIsLastActiveTab(false)
  })

  app.use(Quasar, quasarOptions)
  app.use(i18n)
  app.directive('safe-html', safeHtmlDirective)
  app.mount('#app')

  loadEmojiData().then(() => {
    eventEmitterService.emit(messengerEmojiLoadedEventName)
  })
}

run()
